import { ChangeEvent, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { DownloadIcon, FileIcon, PaperclipIcon, XIcon } from 'lucide-react'

import {
    createInputStorageResources,
    type ExtractedStorageResource,
    type ExtractedStorageResources,
    type InputStorageResources,
} from '@campai/lib-common/entities'
import { formatBytes } from '@campai/lib-common/i18n'
import { type UploadHandler } from '@campai/lib-web/components'
import { Button } from '@campai/lib-web/components/new/ui'

export const extractFileIdFromResource = (resource: string) => {
    return (resource.split('/').pop() ?? '').split('?')[0]
}

const extractMarkdownImageIds = (markdown: string) => {
    const regex = /!\[.*?\]\((.*?)\)/g
    return [...markdown.matchAll(regex)].map((match) =>
        extractFileIdFromResource((match[1] ?? '').split('"')[0].trim())
    )
}

export const splitAttachments = (markdown: string, attachments: ExtractedStorageResources) => {
    const imageIds = new Set(extractMarkdownImageIds(markdown))

    return {
        imageAttachments: attachments.filter((attachment) =>
            imageIds.has(extractFileIdFromResource(attachment.resource))
        ),
        fileAttachments: attachments.filter(
            (attachment) => !imageIds.has(extractFileIdFromResource(attachment.resource))
        ),
    }
}

export const createAttachmentInput = ({
    markdown,
    imageAttachments,
    fileAttachments,
}: {
    markdown: string
    imageAttachments: ExtractedStorageResources
    fileAttachments: ExtractedStorageResources
}): InputStorageResources => {
    const imageIds = new Set(extractMarkdownImageIds(markdown))
    const usedImageAttachments = imageAttachments.filter((attachment) =>
        imageIds.has(extractFileIdFromResource(attachment.resource))
    )

    return createInputStorageResources([...usedImageAttachments, ...fileAttachments])
}

export type AttachmentInput = ReturnType<typeof createAttachmentInput>

export const createRichTextEditorResources = (attachments: ExtractedStorageResources) =>
    attachments.map((attachment) => ({
        id: extractFileIdFromResource(attachment.resource),
        url: attachment.url,
    }))

type AttachmentEditorProps = {
    attachments: ExtractedStorageResources
    disabled?: boolean
    upload: UploadHandler
    onChange: (attachments: ExtractedStorageResources) => void
}

export const AttachmentEditor = ({ attachments, disabled, upload, onChange }: AttachmentEditorProps) => {
    const { t, i18n } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleFilesSelected = useCallback(
        async (event: ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(event.target.files ?? [])
            event.target.value = ''

            const uploadedAttachments: ExtractedStorageResource[] = []
            for (const file of files) {
                const uploadResult = await upload(file)
                if (uploadResult) {
                    uploadedAttachments.push({
                        resource: uploadResult.fileId,
                        url: uploadResult.url,
                        fileName: uploadResult.fileName,
                        contentType: uploadResult.file.type || undefined,
                        fileSizeBytes: uploadResult.file.size || undefined,
                    })
                }
            }

            if (uploadedAttachments.length) {
                onChange([...attachments, ...uploadedAttachments])
            }
        },
        [attachments, onChange, upload]
    )

    return (
        <div className="space-y-2">
            <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(event) => void handleFilesSelected(event)}
            />
            <Button type="button" variant="outline" disabled={disabled} onClick={() => fileInputRef.current?.click()}>
                <PaperclipIcon data-icon="inline-start" />
                {t('product.community.shared.attachments.add')}
            </Button>
            {attachments.length > 0 && (
                <div className="border-border divide-border divide-y overflow-hidden rounded-md border">
                    {attachments.map((attachment) => (
                        <div key={attachment.resource} className="flex items-center gap-3 px-3 py-2 text-sm">
                            <FileIcon className="text-muted-foreground size-4 shrink-0" />
                            <div className="min-w-0 flex-1">
                                <div className="truncate font-medium">
                                    {attachment.fileName || extractFileIdFromResource(attachment.resource)}
                                </div>
                                {attachment.fileSizeBytes ? (
                                    <div className="text-muted-foreground text-xs">
                                        {formatBytes(attachment.fileSizeBytes, { i18n })}
                                    </div>
                                ) : null}
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                title={t('product.community.shared.attachments.remove')}
                                disabled={disabled}
                                onClick={() =>
                                    onChange(
                                        attachments.filter((candidate) => candidate.resource !== attachment.resource)
                                    )
                                }
                            >
                                <XIcon />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

type AttachmentListProps = {
    markdown: string
    attachments: ExtractedStorageResources
}

export const AttachmentList = ({ markdown, attachments }: AttachmentListProps) => {
    const { t, i18n } = useTranslation()
    const { fileAttachments } = splitAttachments(markdown, attachments)

    if (!fileAttachments.length) {
        return null
    }

    return (
        <div className="border-border divide-border mt-4 divide-y overflow-hidden rounded-md border">
            {fileAttachments.map((attachment) => (
                <a
                    key={attachment.resource}
                    href={attachment.url || attachment.resource}
                    download={attachment.fileName}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:bg-muted/50 flex items-center gap-3 px-3 py-2 text-sm"
                >
                    <FileIcon className="text-muted-foreground size-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">
                            {attachment.fileName || extractFileIdFromResource(attachment.resource)}
                        </div>
                        {attachment.fileSizeBytes ? (
                            <div className="text-muted-foreground text-xs">
                                {formatBytes(attachment.fileSizeBytes, { i18n })}
                            </div>
                        ) : null}
                    </div>
                    <DownloadIcon
                        className="text-muted-foreground size-4 shrink-0"
                        aria-label={t('product.community.shared.attachments.download')}
                    />
                </a>
            ))}
        </div>
    )
}
